import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useMemo } from 'react'
import { useParams } from 'react-router'

import type { IOption } from 'common/interfaces/IOption'
import {
  BEVERLY_HILLS_SECOND_PRODUCTION,
  RESIDEN_CAJ_STAGING,
} from 'common/constants/siteSelectorConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import { DateService } from 'common/services/dateService'
import type { IPatientConsultationStaff, IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type {
  IPatientSchedule,
  PatientStatus,
  PunctualityType,
} from 'features/Home/interfaces/IInfoSchedule'
import { useFetchScheduleDayQuery } from 'features/Schedule/api/schedule.api'
import type {
  AppointmentCategories,
  PatientBufferTime,
  ScheduleDay,
} from 'features/Schedule/api/schedule.types'

dayjs.extend(customParseFormat)

const sitesToCheck = [RESIDEN_CAJ_STAGING, BEVERLY_HILLS_SECOND_PRODUCTION]

const { getCurrentTimeInTimezone } = DateService

type TimeInterval = {
  min: string
  max: string
}

type NormalizedChartScheduleDay = {
  hasEhrBooking: boolean
  rooms: IPatientRoom[]
  schedules: IPatientSchedule[]
  consultationStaffs: IPatientConsultationStaff[]
  languages: IOption[]
  appointmentCategories: AppointmentCategories[]
  patientStatuses: PatientStatus[]
  punctuality: PunctualityType[]
  patientBufferTime: PatientBufferTime
  byPassTemperatureCheck: boolean
  timeInterval: TimeInterval
}

const EMPTY_TIME_INTERVAL: TimeInterval = { min: '', max: '' }
const EMPTY_PATIENT_BUFFER_TIME: PatientBufferTime = { after: 0, before: 0 }

const hasTimeInterval = (timeInterval: TimeInterval) => {
  return Boolean(timeInterval.min && timeInterval.max)
}

const preferActiveList = <T>(active: T[], fallback: T[]) => {
  return active.length > 0 ? active : fallback
}

const normalizeTimeInterval = (scheduleDay?: ScheduleDay): TimeInterval => {
  if (!scheduleDay || scheduleDay.time_interval.min === null) return EMPTY_TIME_INTERVAL

  return {
    min: dayjs(scheduleDay.time_interval.min, 'HH:mm:ss').format('HH:mm'),
    max: dayjs(scheduleDay.time_interval.max, 'HH:mm:ss').format('HH:mm'),
  }
}

const normalizeScheduleDay = (scheduleDay?: ScheduleDay): NormalizedChartScheduleDay => {
  return {
    hasEhrBooking: Boolean(scheduleDay?.has_ehr_booking),
    rooms: scheduleDay?.rooms ?? [],
    schedules: scheduleDay?.items ?? [],
    consultationStaffs: scheduleDay?.consultation_staffs ?? [],
    languages: scheduleDay?.languages ?? [],
    appointmentCategories: scheduleDay?.appointment_categories ?? [],
    patientStatuses: scheduleDay?.patient_statuses ?? [],
    punctuality: scheduleDay?.punctuality ?? [],
    patientBufferTime: scheduleDay?.patient_buffer_time ?? EMPTY_PATIENT_BUFFER_TIME,
    byPassTemperatureCheck: scheduleDay?.pandemic_protocol === 0,
    timeInterval: normalizeTimeInterval(scheduleDay),
  }
}

export const useChartScheduleData = (selectedDate?: string) => {
  const { siteId } = useParams()
  const { timeZone } = useTimezone()
  const isJessicaSite = sitesToCheck.includes(siteId)

  const today = useMemo(() => {
    return getCurrentTimeInTimezone(timeZone).format('YYYY-MM-DD')
  }, [timeZone])

  const date = selectedDate || today
  const shouldUseSelectedDate = date !== today

  const { data: todayScheduleDay, isFetching: isFetchingToday } = useFetchScheduleDayQuery(
    { siteId: siteId!, date: today },
    {
      skip: !siteId || !today,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const { data: selectedScheduleDay, isFetching: isFetchingSelectedDate } = useFetchScheduleDayQuery(
    { siteId: siteId!, date },
    {
      skip: !siteId || !date || !shouldUseSelectedDate,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const baseScheduleData = useMemo(
    () => normalizeScheduleDay(todayScheduleDay),
    [todayScheduleDay],
  )
  const selectedScheduleData = useMemo(
    () => normalizeScheduleDay(selectedScheduleDay),
    [selectedScheduleDay],
  )

  const resolvedScheduleData = useMemo<NormalizedChartScheduleDay>(() => {
    if (!shouldUseSelectedDate) {
      return baseScheduleData
    }

    const waitingRoomFallback = baseScheduleData.rooms.filter((room) => room.is_waiting_room)

    return {
      ...baseScheduleData,
      ...selectedScheduleData,
      rooms: preferActiveList(selectedScheduleData.rooms, waitingRoomFallback),
      schedules: selectedScheduleDay ? selectedScheduleData.schedules : [],
      consultationStaffs: preferActiveList(
        selectedScheduleData.consultationStaffs,
        baseScheduleData.consultationStaffs,
      ),
      languages: preferActiveList(selectedScheduleData.languages, baseScheduleData.languages),
      appointmentCategories: preferActiveList(
        selectedScheduleData.appointmentCategories,
        baseScheduleData.appointmentCategories,
      ),
      patientStatuses: preferActiveList(
        selectedScheduleData.patientStatuses,
        baseScheduleData.patientStatuses,
      ),
      punctuality: preferActiveList(selectedScheduleData.punctuality, baseScheduleData.punctuality),
      patientBufferTime: selectedScheduleDay
        ? selectedScheduleData.patientBufferTime
        : baseScheduleData.patientBufferTime,
      byPassTemperatureCheck: selectedScheduleDay
        ? selectedScheduleData.byPassTemperatureCheck
        : baseScheduleData.byPassTemperatureCheck,
      timeInterval: hasTimeInterval(selectedScheduleData.timeInterval)
        ? selectedScheduleData.timeInterval
        : baseScheduleData.timeInterval,
    }
  }, [baseScheduleData, selectedScheduleData, selectedScheduleDay, shouldUseSelectedDate])

  return {
    date,
    isAvailable: baseScheduleData.hasEhrBooking && !isFetchingToday && !!todayScheduleDay,
    isJessicaSite,
    isFetching: isFetchingToday || isFetchingSelectedDate,
    today,
    timeZone,
    rooms: resolvedScheduleData.rooms,
    schedules: resolvedScheduleData.schedules,
    consultationStaffs: resolvedScheduleData.consultationStaffs,
    languages: resolvedScheduleData.languages,
    appointmentCategories: resolvedScheduleData.appointmentCategories,
    patientStatuses: resolvedScheduleData.patientStatuses,
    punctuality: resolvedScheduleData.punctuality,
    patientBufferTime: resolvedScheduleData.patientBufferTime,
    byPassTemperatureCheck: resolvedScheduleData.byPassTemperatureCheck,
    timeInterval: resolvedScheduleData.timeInterval,
  }
}
